/**
 * NewPositionDataContext
 *
 * Provides real-time price data for position execution opportunities.
 * Uses polling to fetch grouped spot-futures price data from the server
 * every 2 seconds.
 *
 * ## KRW/USDT Conversion Rate
 *
 * Uses usePriceUpdates to get real-time prices and calculates:
 *   usdtKrw = upbitBtcKrw / binanceBtcUsdt
 *
 * This provides the effective USDT/KRW exchange rate based on actual
 * market prices rather than a fixed rate.
 */

import React, {
  createContext,
  useContext,
  useMemo,
  ReactNode,
} from 'react';
import { Exchange } from '../types/futuresSpot';
import {
  useSpotFuturesPrices,
  type ConvertedPositionOpportunity,
  type ConvertedSpotExchangeData,
  type PriceLevel,
  type OrderbookData,
  type QuoteToUsdtRates,
} from '../hooks/useSpotFuturesPrices';
import { usePriceUpdates } from '../hooks/usePriceUpdates';

// Re-export types for backward compatibility
export type { PriceLevel, OrderbookData };

/**
 * Spot data for a single exchange
 * (Backward compatible with existing UI components)
 */
export interface SpotExchangeData {
  exchange: Exchange;
  symbol: string;
  ccxtSymbol: string;
  quote: 'USDT' | 'KRW' | 'BTC';
  lastPrice: number | null;
  bestBid: PriceLevel | null;
  bestAsk: PriceLevel | null;
  orderbook: OrderbookData | null;
  differentialPercent: number | null;
  differentialAbsolute: number | null;
}

/**
 * Position opportunity for a single base asset
 * (Backward compatible with existing UI components)
 */
export interface PositionOpportunity {
  baseAsset: string;
  futuresSymbol: string;
  futuresCcxtSymbol: string;
  futuresBestBid: PriceLevel | null;
  futuresBestAsk: PriceLevel | null;
  futuresOrderbook: OrderbookData | null;
  fundingRate: number | null;
  nextFundingTime: number | null;
  fundingIntervalHours: number | null;
  spotData: SpotExchangeData[];
  bestDifferentialPercent: number | null;
  bestDifferentialExchange: Exchange | null;
  timestamp: number;
}

/**
 * Connection status type (simplified for polling)
 */
type ConnectionStatus = 'connected' | 'disconnected' | 'connecting' | 'reconnecting';

/**
 * Context value
 */
interface NewPositionContextValue {
  // Position opportunities
  opportunities: PositionOpportunity[];

  // Connection status per exchange (simplified for polling)
  connectionStatus: {
    binanceFutures: ConnectionStatus;
    binanceSpot: ConnectionStatus;
    upbit: ConnectionStatus;
    bithumb: ConnectionStatus;
  };

  // State
  loading: boolean;
  error: string | null;

  // Stats
  totalSymbols: number;
  connectedExchanges: number;

  // Conversion rates
  usdtKrwRate: number | null;
  btcUsdtRate: number | null;

  // Last update timestamp
  lastUpdate: number;

  // Manual refresh
  refetch: () => Promise<void>;
}

const NewPositionDataContext = createContext<NewPositionContextValue | undefined>(undefined);

/**
 * Convert internal type to export type
 * Note: Server returns minimal data, we provide null for missing fields
 */
function convertSpotData(data: ConvertedSpotExchangeData, baseAsset: string): SpotExchangeData {
  // Derive symbol from exchange and quote
  const quoteStr = data.quote;
  const symbol = `${baseAsset}${quoteStr}`;
  const ccxtSymbol = `${baseAsset}/${quoteStr}`;

  return {
    exchange: data.exchange,
    symbol,
    ccxtSymbol,
    quote: data.quote,
    lastPrice: data.lastPrice,
    bestBid: null, // Not provided by server
    bestAsk: data.bestAsk,
    orderbook: null, // Not provided by server
    differentialPercent: data.differentialPercent,
    differentialAbsolute: null, // Not calculated
  };
}

function convertOpportunity(opp: ConvertedPositionOpportunity): PositionOpportunity {
  return {
    baseAsset: opp.baseAsset,
    futuresSymbol: opp.futuresSymbol,
    futuresCcxtSymbol: opp.futuresCcxtSymbol,
    futuresBestBid: opp.futuresBestBid,
    futuresBestAsk: null, // Not provided by server (we only need bid for shorting)
    futuresOrderbook: null, // Not provided by server
    fundingRate: opp.fundingRate,
    nextFundingTime: opp.nextFundingTime,
    fundingIntervalHours: opp.fundingIntervalHours,
    spotData: opp.spotData.map((s) => convertSpotData(s, opp.baseAsset)),
    bestDifferentialPercent: opp.bestDifferentialPercent,
    bestDifferentialExchange: opp.bestDifferentialExchange,
    timestamp: opp.timestamp,
  };
}

/**
 * Provider Props
 */
interface NewPositionDataProviderProps {
  children: ReactNode;
  enabled?: boolean;
  enableLogging?: boolean;
  pollingInterval?: number;
}

/**
 * NewPositionDataProvider Component
 *
 * Uses server-side polling for price data instead of WebSocket connections.
 * Much simpler and more reliable than the previous approach.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <NewPositionDataProvider>
 *       <PositionOpportunitiesTable />
 *     </NewPositionDataProvider>
 *   );
 * }
 * ```
 */
export const NewPositionDataProvider: React.FC<NewPositionDataProviderProps> = ({
  children,
  enabled = true,
  enableLogging = false,
  pollingInterval = 3000,
}) => {
  // Get real-time prices from WebSocket for conversion rates
  const headerPrices = usePriceUpdates();

  // Calculate conversion rates from header prices
  // usdtKrw = upbitBtcKrw / binanceBtcUsdt
  const rates: QuoteToUsdtRates = useMemo(() => {
    const upbitBtcKrw = headerPrices?.upbitBtcKrw ?? null;
    const binanceBtcUsdt = headerPrices?.binanceBtcUsdt ?? null;

    // Calculate USDT/KRW rate from BTC prices
    const usdtKrw = (upbitBtcKrw && binanceBtcUsdt && binanceBtcUsdt > 0)
      ? upbitBtcKrw / binanceBtcUsdt
      : null;

    return {
      usdtKrw,
      btcUsdt: binanceBtcUsdt,
    };
  }, [headerPrices?.upbitBtcKrw, headerPrices?.binanceBtcUsdt]);

  // Use the polling hook with conversion rates
  const {
    opportunities: rawOpportunities,
    loading,
    error,
    lastUpdate,
    refetch,
  } = useSpotFuturesPrices(rates, enabled, pollingInterval);

  // Convert opportunities to backward-compatible format
  const opportunities = useMemo(() => {
    return rawOpportunities.map(convertOpportunity);
  }, [rawOpportunities]);

  // Log for debugging
  if (enableLogging && lastUpdate > 0) {
    console.log('[NewPositionDataContext] Polling update:', {
      opportunities: opportunities.length,
      usdtKrw: rates.usdtKrw,
      btcUsdt: rates.btcUsdt,
      loading,
      error,
    });
  }

  // Determine connection status based on data availability
  // Simplified: if we have data, all exchanges are connected
  const hasData = opportunities.length > 0;
  const connectionStatus = useMemo(() => {
    if (loading && !hasData) {
      return {
        binanceFutures: 'connecting' as ConnectionStatus,
        binanceSpot: 'connecting' as ConnectionStatus,
        upbit: 'connecting' as ConnectionStatus,
        bithumb: 'connecting' as ConnectionStatus,
      };
    }

    if (error && !hasData) {
      return {
        binanceFutures: 'disconnected' as ConnectionStatus,
        binanceSpot: 'disconnected' as ConnectionStatus,
        upbit: 'disconnected' as ConnectionStatus,
        bithumb: 'disconnected' as ConnectionStatus,
      };
    }

    // Simplified: if we have data, assume all are connected
    // Avoid iterating through all opportunities on every render
    return {
      binanceFutures: 'connected' as ConnectionStatus,
      binanceSpot: 'connected' as ConnectionStatus,
      upbit: 'connected' as ConnectionStatus,
      bithumb: 'connected' as ConnectionStatus,
    };
  }, [loading, error, hasData]);

  // Count connected exchanges
  const connectedExchanges = useMemo(() => {
    let count = 0;
    if (connectionStatus.binanceFutures === 'connected') count++;
    if (connectionStatus.binanceSpot === 'connected') count++;
    if (connectionStatus.upbit === 'connected') count++;
    if (connectionStatus.bithumb === 'connected') count++;
    return count;
  }, [connectionStatus]);

  // Total symbols count (from opportunities)
  const totalSymbols = opportunities.length;

  const value: NewPositionContextValue = {
    opportunities,
    connectionStatus,
    loading,
    error,
    totalSymbols,
    connectedExchanges,
    usdtKrwRate: rates.usdtKrw,
    btcUsdtRate: rates.btcUsdt,
    lastUpdate,
    refetch,
  };

  return (
    <NewPositionDataContext.Provider value={value}>
      {children}
    </NewPositionDataContext.Provider>
  );
};

/**
 * Hook: useNewPositionData
 *
 * Returns the full context for position opportunities.
 *
 * @throws Error if used outside NewPositionDataProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useNewPositionData = () => {
  const context = useContext(NewPositionDataContext);
  if (!context) {
    throw new Error('useNewPositionData must be used within NewPositionDataProvider');
  }
  return context;
};
